/**
 * Study Timer + nhắc nghỉ mắt — Prompt.md §23.
 *
 * Không dùng `setInterval`: timer ở đây là một **hàm thuần trên thời gian đã trôi**, được hỏi mỗi
 * lần có frame mới. Nhờ vậy nó không bị lệch khi tab bị iOS Safari đóng băng (timer nền bị
 * throttle nặng), và test được bằng cách bơm số thay vì chờ thật.
 */

import { defaultSessionConfig, type SessionConfig } from "@studystraight/shared-config";
import type { SessionOptions, TimerPreset } from "@studystraight/shared-types";

export type TimerEvent = "EYE_BREAK" | "GOAL_REACHED";

export interface TimerTick {
  /** ms đã học, đã trừ thời gian pause. */
  elapsedMs: number;
  /** ms còn lại tới mục tiêu; `null` khi phiên không đặt mục tiêu. */
  remainingMs: number | null;
  events: readonly TimerEvent[];
}

/** §23 — preset ra ms. `custom` cần `customDurationMs`; thiếu thì coi như không đặt mục tiêu. */
export function plannedDurationMs(options: SessionOptions): number | undefined {
  return resolvePreset(options.timerPreset, options.customDurationMs);
}

function resolvePreset(preset: TimerPreset, customDurationMs?: number): number | undefined {
  if (preset === "custom") {
    return customDurationMs !== undefined && customDurationMs > 0 ? customDurationMs : undefined;
  }
  return preset * 60_000;
}

export interface StudyTimerOptions {
  plannedDurationMs?: number;
  eyeBreakEnabled: boolean;
  config?: SessionConfig;
}

export class StudyTimer {
  private readonly config: SessionConfig;
  private eyeBreaks = 0;
  private goalReached = false;

  constructor(private readonly options: StudyTimerOptions) {
    this.config = options.config ?? defaultSessionConfig;
  }

  get eyeBreakCount(): number {
    return this.eyeBreaks;
  }

  /**
   * Hỏi timer tại một mốc thời gian đã học.
   *
   * Mỗi mốc nghỉ mắt chỉ phát **một lần** dù `elapsedMs` nhảy vọt (tab ngủ 30 phút rồi tỉnh lại):
   * đếm số mốc đã đi qua rồi so với số lần đã phát, thay vì so `elapsedMs % interval` — cách sau
   * sẽ bỏ mất mốc mỗi khi khoảng nhảy lớn hơn một chu kỳ.
   */
  tick(elapsedMs: number): TimerTick {
    const events: TimerEvent[] = [];

    if (this.options.eyeBreakEnabled) {
      const due = Math.floor(elapsedMs / this.config.eyeBreakIntervalMs);
      if (due > this.eyeBreaks) {
        // Chỉ phát một lần cho lần tick này: nhắc dồn 3 lần liền vì tab vừa ngủ là vô nghĩa.
        this.eyeBreaks = due;
        events.push("EYE_BREAK");
      }
    }

    const planned = this.options.plannedDurationMs;
    if (planned !== undefined && !this.goalReached && elapsedMs >= planned) {
      this.goalReached = true;
      events.push("GOAL_REACHED");
    }

    return {
      elapsedMs,
      // Không kẹp ở 0 phía trên nhưng kẹp ở đây: học quá mục tiêu là chuyện tốt, không phải nợ.
      remainingMs: planned === undefined ? null : Math.max(0, planned - elapsedMs),
      events,
    };
  }

  reset(): void {
    this.eyeBreaks = 0;
    this.goalReached = false;
  }
}
